import {
  Archive,
  BookOpen,
  BrainCircuit,
  CalendarClock,
  ExternalLink,
  LayoutGrid,
  MessagesSquare,
  MonitorCheck,
  Radar,
  RadioTower,
  Wrench,
} from "lucide-react";
import { useEffect, useState, type ComponentType } from "react";
import { useNavigate } from "react-router-dom";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command";
import { useVendorPages } from "@/hooks/useVendorPages";
import type { VendorPage } from "@/lib/api";

/** Spotlight-style command palette. Cmd+K / Ctrl+K / `/` toggles it.
 *
 *  Entries are computed once from the static page list + vendor page catalog.
 *  Selecting an item navigates + closes.
 *
 *  Keybinding rules:
 *    - Cmd/Ctrl+K: always opens (universal)
 *    - `/`: opens unless the user is currently typing into an input,
 *      textarea, or contenteditable — we don't want to hijack regular
 *      typing. Same convention as GitHub, Linear, Vercel, etc.
 *    - Esc: closes (handled by DialogPrimitive)
 */

interface Target {
  /** Unique id for cmdk keying + value. */
  id: string;
  /** Shown in the palette. */
  label: string;
  /** Extra searchable tokens joined into cmdk's value (section, vendor). */
  keywords: string[];
  /** Section heading. */
  group: string;
  /** Icon rendered left of the label. */
  icon: ComponentType<{ className?: string }>;
  /** Where to navigate on select. */
  path: string;
}

function buildTargets(vendorPages: VendorPage[]): Target[] {
  const pages: Target[] = [
    {
      id: "page:inbox",
      label: "Signal Inbox",
      keywords: ["signals", "inbox", "triage"],
      group: "Pages",
      icon: Archive,
      path: "/inbox",
    },
    {
      id: "page:candidates",
      label: "Candidates",
      keywords: ["signals", "candidates", "symbols", "trade plans"],
      group: "Pages",
      icon: BrainCircuit,
      path: "/candidates",
    },
    {
      id: "page:tools",
      label: "Tools",
      keywords: ["dashboard", "iframes", "vendor"],
      group: "Pages",
      icon: Wrench,
      path: "/tools",
    },
    {
      id: "page:chats",
      label: "Chat rooms",
      keywords: ["vendor", "chat", "rooms", "popout"],
      group: "Pages",
      icon: MessagesSquare,
      path: "/chats",
    },
    {
      id: "page:sources",
      label: "Sources",
      keywords: ["admin", "yaml", "scrapers", "scheduling"],
      group: "Admin",
      icon: LayoutGrid,
      path: "/sources",
    },
    {
      id: "page:source-registry",
      label: "Source registry",
      keywords: ["admin", "scrape", "sources", "operations"],
      group: "Admin",
      icon: LayoutGrid,
      path: "/admin/sources",
    },
    {
      id: "page:vendor-pages",
      label: "Vendor pages",
      keywords: ["admin", "tools", "chat rooms", "scrape", "iframe", "catalog"],
      group: "Admin",
      icon: Wrench,
      path: "/admin/vendor-pages",
    },
    {
      id: "page:operator-status",
      label: "Operator Status",
      keywords: ["ops", "health", "scraper", "scheduler", "status"],
      group: "Operator",
      icon: MonitorCheck,
      path: "/operator/status",
    },
    {
      id: "page:operator-scraper",
      label: "Scraper",
      keywords: ["ops", "scraper", "vendor pages", "run", "analyze", "worker"],
      group: "Operator",
      icon: Radar,
      path: "/operator/scraper",
    },
    {
      id: "page:operator-webhooks",
      label: "Webhooks",
      keywords: ["ops", "webhook", "tradingview", "trendspider", "tradeideas", "push"],
      group: "Operator",
      icon: RadioTower,
      path: "/operator/webhooks",
    },
    {
      id: "page:operator-processor",
      label: "Processor",
      keywords: ["ops", "processor", "llm", "extract", "analyze", "candidates"],
      group: "Operator",
      icon: BrainCircuit,
      path: "/operator/processor",
    },
    {
      id: "page:operator-publish",
      label: "Publish",
      keywords: ["ops", "publish", "outbox", "targets", "trading bot"],
      group: "Operator",
      icon: RadioTower,
      path: "/operator/publish",
    },
    {
      id: "page:operator-schedules",
      label: "Scrape Runtime",
      keywords: ["ops", "scheduler", "cadence", "scrape", "cron", "warm page"],
      group: "Operator",
      icon: CalendarClock,
      path: "/operator/schedules",
    },
  ];

  const toolItems: Target[] = vendorPages
    .filter((vendorPage) => vendorPage.page_type === "tool")
    .map((vendorPage) => {
      const isInApp =
        (vendorPage.access_mode === "iframe" ||
          vendorPage.access_mode === "scripted_iframe") &&
        vendorPage.iframe_url !== null;
      return {
        id: `tool:${vendorPage.id}`,
        label: displayLabel(vendorPage),
        keywords: vendorPageKeywords(vendorPage),
        group: "Tools",
        icon: Wrench,
        path: isInApp ? `/tools/${vendorPage.id}` : vendorPage.url,
      };
    });

  const roomItems: Target[] = vendorPages
    .filter((vendorPage) =>
      ["chat_room", "room"].includes(vendorPage.page_type),
    )
    .map((vendorPage) => ({
      id: `${vendorPage.page_type}:${vendorPage.id}`,
      label: displayLabel(vendorPage),
      keywords: vendorPageKeywords(vendorPage),
      group: vendorPage.page_type === "chat_room" ? "Chat rooms" : "Rooms",
      icon: MessagesSquare,
      path: vendorPage.url,
    }));

  const knowledgeItems: Target[] = vendorPages
    .filter((vendorPage) => vendorPage.page_type === "knowledge")
    .map((vendorPage) => ({
      id: `knowledge:${vendorPage.id}`,
      label: displayLabel(vendorPage),
      keywords: vendorPageKeywords(vendorPage),
      group: "Knowledge",
      icon: BookOpen,
      path: vendorPage.url,
    }));

  const externalItems: Target[] = vendorPages
    .filter((vendorPage) => vendorPage.page_type === "external_app")
    .map((vendorPage) => ({
      id: `external:${vendorPage.id}`,
      label: displayLabel(vendorPage),
      keywords: vendorPageKeywords(vendorPage),
      group: "External apps",
      icon: ExternalLink,
      path: vendorPage.url,
    }));

  return [...pages, ...toolItems, ...roomItems, ...knowledgeItems, ...externalItems];
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key === "k") {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      // `/` opens iff we're not already typing somewhere
      if (e.key === "/" && !isTypingTarget(e.target)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    const onOpenPalette = () => setOpen(true);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("signals:open-command-palette", onOpenPalette);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("signals:open-command-palette", onOpenPalette);
    };
  }, []);

  const vendorPagesQ = useVendorPages();
  const targets = buildTargets(vendorPagesQ.data ?? []);
  const groups = Array.from(new Set(targets.map((t) => t.group)));

  const go = (path: string) => {
    setOpen(false);
    // Absolute URLs (external tools) open in a new tab; in-app paths
    // use the router. Matches the sidebar's external-vs-internal split.
    if (path.startsWith("http://") || path.startsWith("https://")) {
      window.open(path, "_blank", "noopener,noreferrer");
    } else {
      navigate(path);
    }
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Jump to page or tool…" />
      <CommandList>
        <CommandEmpty>No matches.</CommandEmpty>
        {groups.map((group) => (
          <CommandGroup key={group} heading={group}>
            {targets
              .filter((t) => t.group === group)
              .map((t) => {
                const Icon = t.icon;
                return (
                  <CommandItem
                    key={t.id}
                    // cmdk matches against this string; include keywords
                    // so vendor/section names count as search hits too.
                    value={[t.label, ...t.keywords].join(" ")}
                    onSelect={() => go(t.path)}
                  >
                    <Icon />
                    <span>{t.label}</span>
                    {group === "Tools" ||
                    group === "Chat rooms" ||
                    group === "Rooms" ? (
                      <CommandShortcut>{t.id.split(":")[1]}</CommandShortcut>
                    ) : null}
                  </CommandItem>
                );
              })}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}

function vendorPageKeywords(vendorPage: VendorPage): string[] {
  return [
    vendorPage.vendor,
    vendorPage.vendor_display_name,
    vendorPage.subscription,
    vendorPage.section,
    vendorPage.id,
    vendorPage.label,
    ...(vendorPage.notes ? [vendorPage.notes] : []),
  ];
}

function displayLabel(vendorPage: VendorPage): string {
  return vendorPage.nav_label ?? vendorPage.label;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}
